import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Form, Row, Col, App, Spin } from "antd";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import CustomButton from "../../components/CustomButton/Button";
import FilePreviewModal from "../../components/DocUploadCard/FilePreviewModal";
import axios from "../../config/httpConfig";
import {
    getJvBusinesses,
    getJvDealsByBusiness,
    getJvInvestorsByDeal,
    uploadJvReport,
    getJvReports,
    clearDeals,
    clearInvestors,
} from "../../redux/slices/JvReportsSlice";
import "./Reports.scss";

const { Title, Text } = Typography;

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];

const isAllowedFile = (file) => {
    if (ALLOWED_TYPES.includes(file.type)) return true;
    const name = (file.name || "").toLowerCase();
    const hasAllowedExt = ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
    if ((!file.type || file.type === "application/octet-stream") && hasAllowedExt) {
        return true;
    }
    return false;
};

const JvReports = () => {
    const dispatch = useDispatch();
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const fileInputRef = useRef(null);

    const {
        businesses,
        deals,
        investors,
        reports,
        businessesLoading,
        dealsLoading,
        investorsLoading,
        uploadLoading,
        reportsLoading,
    } = useSelector((state) => state.jvReports);

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewTitle, setPreviewTitle] = useState("Document Preview");
    const [viewingReportId, setViewingReportId] = useState(null);

    const selectedBusiness = Form.useWatch("businessId", form);
    const selectedDeal = Form.useWatch("dealId", form);
    const selectedInvestor = Form.useWatch("investorId", form);

    const isUploadEnabled = Boolean(selectedInvestor);

    const resetFileInput = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const closePreview = () => {
        setPreviewOpen(false);
        setPreviewFile(null);
        setPreviewTitle("Document Preview");
    };

    useEffect(() => {
        dispatch(getJvBusinesses());
    }, [dispatch]);

    useEffect(() => {
        if (!selectedBusiness || !selectedDeal) return;

        const params = {
            businessId: selectedBusiness,
            dealId: selectedDeal,
        };
        if (selectedInvestor) {
            params.investorId = selectedInvestor;
        }

        dispatch(getJvReports(params));
    }, [dispatch, selectedBusiness, selectedDeal, selectedInvestor]);

    const handleBusinessChange = (value) => {
        form.setFieldsValue({ dealId: undefined, investorId: undefined });
        resetFileInput();
        closePreview();
        dispatch(clearDeals());
        dispatch(getJvDealsByBusiness(value));
    };

    const handleDealChange = (value) => {
        form.setFieldsValue({ investorId: undefined });
        resetFileInput();
        closePreview();
        dispatch(clearInvestors());
        dispatch(
            getJvInvestorsByDeal({
                dealId: value,
                businessId: form.getFieldValue("businessId"),
            })
        );
    };

    const handleInvestorChange = () => {
        resetFileInput();
        closePreview();
    };

    const hasAnyBusinessWithInvestors = useMemo(
        () => businesses.some((b) => b.hasInvestors),
        [businesses]
    );

    const isBusinessSelectDisabled =
        !businessesLoading && businesses.length > 0 && !hasAnyBusinessWithInvestors;

    const businessOptions = useMemo(
        () =>
            businesses.map((b) => ({
                value: b.orgId,
                label: b.orgName,
                disabled: !b.hasInvestors,
            })),
        [businesses]
    );

    const dealOptions = useMemo(
        () =>
            deals.map((d) => ({
                value: d.dealId,
                label: d.dealName || d.dealId,
            })),
        [deals]
    );

    const investorOptions = useMemo(
        () => investors.map((inv) => ({ value: inv.orgId, label: inv.contactName })),
        [investors]
    );

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (!files?.length) return;

        if (files.length > 1) {
            message.error("Only one file can be uploaded at a time");
            resetFileInput();
            return;
        }

        const file = files[0];

        if (!isAllowedFile(file)) {
            message.error("Only PDF or Docx files are allowed");
            resetFileInput();
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            message.error("File size must not exceed 2MB");
            resetFileInput();
            return;
        }

        setSelectedFile(file);
    };

    const handleRemoveFile = () => {
        resetFileInput();
    };

    const handleViewSelectedFile = () => {
        if (!selectedFile) return;
        setPreviewFile(selectedFile);
        setPreviewTitle(selectedFile.name || "Document Preview");
        setPreviewOpen(true);
    };

    const handleViewUploadedReport = async (report) => {
        if (!report?.reportId) return;
        try {
            setViewingReportId(report.reportId);
            const blob = await axios.get("/api/admin/jvreports/view", {
                params: { reportId: report.reportId },
                responseType: "blob",
            });

            if (blob?.type === "application/json") {
                const text = await blob.text();
                const result = JSON.parse(text);
                throw new Error(result?.error || result?.message || "Failed to open report");
            }

            const fileName = report.reportName || "jv-report";
            const file = new File([blob], fileName, { type: blob.type || "application/pdf" });
            setPreviewFile(file);
            setPreviewTitle(fileName);
            setPreviewOpen(true);
        } catch (err) {
            message.error(err?.message || "Failed to open report preview");
        } finally {
            setViewingReportId(null);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!selectedFile) {
                message.warning("Please upload a file first");
                return;
            }

            const formData = new FormData();
            formData.append("businessId", values.businessId);
            formData.append("dealId", values.dealId);
            formData.append("investorId", values.investorId);
            formData.append("file", selectedFile);

            const result = await dispatch(uploadJvReport(formData));
            if (uploadJvReport.fulfilled.match(result)) {
                message.success("JV Report uploaded successfully");
                resetFileInput();
                dispatch(
                    getJvReports({
                        businessId: values.businessId,
                        dealId: values.dealId,
                        investorId: values.investorId,
                    })
                );
            } else {
                message.error(result?.payload?.message || "Upload failed. Please try again.");
            }
        } catch {
            // form validation error — antd shows inline errors
        }
    };

    return (
        <div className="jv-reports-upload" style={{ marginTop: 32 }}>
            <Title level={4} style={{ marginBottom: 16 }}>
                JV Reports
            </Title>
            <div className="jv-reports-upload-card">
                <Form form={form} layout="vertical">
                    <Row gutter={[16, 0]} align="bottom">
                        <Col xs={24} md={12} lg={6}>
                            <CustomSelect
                                label="Business Name"
                                name="businessId"
                                placeholder="Select Business"
                                isRequired
                                rules={[{ required: true, message: "Please select a business" }]}
                                options={businessOptions}
                                loading={businessesLoading}
                                showSearch
                                disabled={isBusinessSelectDisabled}
                                onChange={handleBusinessChange}
                            />
                        </Col>

                        <Col xs={24} md={12} lg={6}>
                            <CustomSelect
                                label="Deals"
                                name="dealId"
                                placeholder="Select Deal"
                                isRequired
                                rules={[{ required: true, message: "Please select a deal" }]}
                                options={dealOptions}
                                loading={dealsLoading}
                                showSearch
                                disabled={!selectedBusiness}
                                onChange={handleDealChange}
                            />
                        </Col>

                        <Col xs={24} md={12} lg={6}>
                            <CustomSelect
                                label="Investor"
                                name="investorId"
                                placeholder="Select Investor"
                                isRequired
                                rules={[{ required: true, message: "Please select an investor" }]}
                                options={investorOptions}
                                loading={investorsLoading}
                                showSearch
                                disabled={!selectedDeal}
                                onChange={handleInvestorChange}
                            />
                        </Col>

                        <Col xs={24} md={12} lg={6}>
                            <Form.Item label=" ">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple={false}
                                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    style={{ display: "none" }}
                                    onChange={handleFileChange}
                                />
                                {selectedFile ? (
                                    <CustomButton
                                        type="primary"
                                        onClick={handleSubmit}
                                        loading={uploadLoading}
                                        disabled={!isUploadEnabled}
                                        block
                                    >
                                        Submit
                                    </CustomButton>
                                ) : (
                                    <CustomButton
                                        type="primary"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={!isUploadEnabled}
                                        block
                                    >
                                        Upload
                                    </CustomButton>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>

                    {selectedFile && (
                        <div className="jv-reports-selected-file">
                            <button
                                type="button"
                                className="jv-reports-selected-file-name"
                                onClick={handleViewSelectedFile}
                                title={`View ${selectedFile.name}`}
                            >
                                {selectedFile.name}
                            </button>
                            <button
                                type="button"
                                className="jv-reports-selected-file-action"
                                onClick={handleViewSelectedFile}
                                aria-label="View selected file"
                                disabled={uploadLoading}
                            >
                                <EyeOutlined />
                            </button>
                            <button
                                type="button"
                                className="jv-reports-selected-file-delete"
                                onClick={handleRemoveFile}
                                aria-label="Remove selected file"
                                disabled={uploadLoading}
                            >
                                <DeleteOutlined />
                            </button>
                        </div>
                    )}

                    <Text type="secondary" className="jv-reports-hint">
                        File size should be max 2MB, Accepted files: PDF, Docx.
                    </Text>
                </Form>
            </div>

            {selectedBusiness && selectedDeal && (
                <div className="jv-reports-uploaded-list">
                    <Title level={5} style={{ marginBottom: 12 }}>
                        {selectedInvestor
                            ? "Uploaded files for selected investor"
                            : "Uploaded files for selected deal"}
                    </Title>
                    {reportsLoading ? (
                        <div style={{ textAlign: "center", padding: 16 }}>
                            <Spin size="small" />
                        </div>
                    ) : reports.length === 0 ? (
                        <Text type="secondary">
                            {selectedInvestor
                                ? "No reports uploaded for this investor yet."
                                : "No reports uploaded for this deal yet."}
                        </Text>
                    ) : (
                        reports.map((report) => (
                            <div key={report.reportId} className="jv-reports-uploaded-row">
                                <div className="jv-reports-uploaded-meta">
                                    <button
                                        type="button"
                                        className="jv-reports-selected-file-name"
                                        onClick={() => handleViewUploadedReport(report)}
                                        title={`View ${report.reportName}`}
                                        disabled={viewingReportId === report.reportId}
                                    >
                                        {report.reportName}
                                    </button>
                                    <Text type="secondary" className="jv-reports-investor-name">
                                        Investor: {report.investorName || report.investorId || "—"}
                                    </Text>
                                </div>
                                <button
                                    type="button"
                                    className="jv-reports-selected-file-action"
                                    onClick={() => handleViewUploadedReport(report)}
                                    aria-label={`View ${report.reportName}`}
                                    disabled={viewingReportId === report.reportId}
                                >
                                    <EyeOutlined />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}

            <FilePreviewModal
                open={previewOpen}
                onClose={closePreview}
                file={previewFile}
                title={previewTitle}
            />
        </div>
    );
};

export default JvReports;
